import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import { Loader } from "../../Common/Loader";
import { X, Loader as LoaderIcon } from "lucide-react";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";
import DeleteModal from "Common/DeleteModal";
import moment from "moment";
import Drawer from "Common/Components/Drawer";
import { addStudent, updateStudent, getClassList, getProgramList } from "helpers/backend_helper";
import { useAuth } from "hooks/useStore";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import Select from "react-select";

type Props = { onHide: () => void; show: boolean, data: any; onSave: (result: any) => void };
export const AddStudent: React.FC<Props> = ({ onHide, show, data, onSave }) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [studentData, setStudentData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [classes, setClasses] = useState<any[]>([]);
  const [classOptions, setClassOptions] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [programOptions, setProgramOptions] = useState<any[]>([]);

  const { user } = useAuth();
  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const uiHelper = new Common();

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" }
  ];

  const stateOptions = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" }
  ];

  useEffect(() => {
    loadPrograms();
    loadClasses();
    if (data?.studentId) {
      setIsEdit(true);
      loadStudent();
    } else {
      setIsEdit(false);
      setStudentData({});
      validation.resetForm();
    }
  }, [data]);

  const loadStudent = async () => {
    setIsLoading(true);
    try {
      setStudentData(data);

      // Set form values
      validation.setValues({
        studentId: data.studentId,
        clientId: data.clientId || 1,
        firstName: data.firstName,
        lastName: data.lastName,
        streetAddress: data.streetAddress || '',
        address1: data.address1 || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        phone1: data.phone1 || '',
        phone2: data.phone2 || '',
        email: data.email || '',
        gender: data.gender || 'Male',
        guardian: data.guardian || '',
        guardianPhone: data.guardianPhone || '',
        emergencyContactName: data.emergencyContactName || '',
        emergencyContactPhone: data.emergencyContactPhone || '',
        startDate: data.startDate ? moment(data.startDate).format("YYYY-MM-DD") : "",
        dob: data.dob ? moment(data.dob).format("YYYY-MM-DD") : "",
        registrationFee: data.registrationFee || 0,
        fees: data.fees || 0,
        discount: data.discount || 0,
        profilePic: data.profilePic || '',
        notes: data.notes || '',
        programId: data.programId || '',
        classId: data.classId || ''
      });
    } catch (error) {
      console.error("Error loading student:", error);
      uiHelper.showErrorMessage("Error loading student data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadPrograms = async () => {
    try {
      const response = await getProgramList();
      setPrograms(response.data);
      // Convert programs to react-select format
      const options = response.data.map((program: any) => ({
        value: program.programId,
        label: program.programName
      }));
      setProgramOptions(options);
    } catch (error) {
      console.error("Error loading programs:", error);
      uiHelper.showErrorMessage("Error loading programs");
    }
  };

  const loadClasses = async () => {
    try {
      const response = await getClassList();
      setClasses(response.data);
      // Convert classes to react-select format
      const options = response.data.map((cls: any) => ({
        value: cls.classId,
        label: cls.className
      }));
      setClassOptions(options);
    } catch (error) {
      console.error("Error loading classes:", error);
      uiHelper.showErrorMessage("Error loading classes");
    }
  };

  const handleDrawer = () => { onHide(); }

  // validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      studentId: (data && data.studentId) || 0,
      clientId: (data && data.clientId) || 1,
      firstName: (data && data.firstName) || "",
      lastName: (data && data.lastName) || "",
      streetAddress: (data && data.streetAddress) || "",
      address1: (data && data.address1) || "",
      city: (data && data.city) || "",
      state: (data && data.state) || "",
      zip: (data && data.zip) || "",
      phone1: (data && data.phone1) || "",
      phone2: (data && data.phone2) || "",
      email: (data && data.email) || "",
      gender: (data && data.gender) || "Male",
      guardian: (data && data.guardian) || "",
      guardianPhone: (data && data.guardianPhone) || "",
      emergencyContactName: (data && data.emergencyContactName) || "",
      emergencyContactPhone: (data && data.emergencyContactPhone) || "",
      startDate: (data && data.startDate) ? moment(data.startDate).format("YYYY-MM-DD") : "",
      dob: (data && data.dob) ? moment(data.dob).format("YYYY-MM-DD") : "",
      registrationFee: (data && data.registrationFee) || 0,
      fees: (data && data.fees) || 0,
      discount: (data && data.discount) || 0,
      profilePic: (data && data.profilePic) || "",
      notes: (data && data.notes) || "",
      programId: (data && data.programId) || "",
      classId: (data && data.classId) || ""
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter first name"),
      lastName: Yup.string().required("Please enter last name"),
      email: Yup.string().email("Please enter a valid email"),
      phone1: Yup.string()
        .required("Please enter primary phone number")
        .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
        .length(10, "Phone number must be exactly 10 digits"),
      phone2: Yup.string()
        .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
        .test('len', 'Phone number must be exactly 10 digits', val => !val || val.length === 10),
      city: Yup.string().required("Please enter city"),
      state: Yup.string().required("Please select state"),
      zip: Yup.string().required("Please enter zip code"),
      gender: Yup.string().required("Please select gender"),
      startDate: Yup.string().required("Please select start date"),
      dob: Yup.string().required("Please select date of birth"),
      registrationFee: Yup.number().min(0, "Registration fee must be greater than or equal to 0").required("Please enter registration fee"),
      fees: Yup.number().min(0, "Fees must be greater than or equal to 0").required("Please enter fees"),
      discount: Yup.number().min(0, "Discount must be greater than or equal to 0"),
      programId: Yup.string().required("Please select a program"),
      classId: Yup.string().required("Please select a class"),
      guardianPhone: Yup.string()
        .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
        .test('len', 'Phone number must be exactly 10 digits', val => !val || val.length === 10),
      emergencyContactPhone: Yup.string()
        .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
        .test('len', 'Phone number must be exactly 10 digits', val => !val || val.length === 10)
    }),
    onSubmit: (values) => {
      handleSave(values);
    }
  });

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      const formData = {
        ...values,
        createdBy: user?.userId ?? 1,
        updatedBy: user?.userId ?? 1
      };

      let response;
      if (isEdit && values.studentId) {
        // Update existing student
        response = await updateStudent(formData);
      } else {
        // Create new student
        response = await addStudent(formData);
      }
      
      uiHelper.showSuccessMessage(
        `Student has been successfully ${isEdit ? 'updated' : 'saved'}.`
      );

      onSave(response.data);
      //onHide();
    } catch (error) {
      console.error("Error saving student:", error);
      uiHelper.showErrorMessage("Error saving student data");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <React.Fragment>
      <Drawer show={show} onHide={handleDrawer} id="studentSidePenal" drawer-end="true" className="fixed inset-y-0 flex flex-col  
      transition-transform duration-600 ease-in-out transform bg-white shadow 
     dark:bg-zink-600 ltr:right-0 rtl:left-0  w-5/6 flex  z-drawer">

        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="grow">
            <h5 className="mb-0 text-16">{isEdit ? "Edit" : "Add"} Student</h5>
          </div>
          <div className="shrink-0">
            <div className="flex items-center justify-end gap-[200px]">
              <div className="flex items-center gap-2 group">

              </div>
              <Drawer.Header data-drawer-close="studentSidePenal" className="transition-all duration-150 ease-linear text-slate-500 hover:text-slate-800">
                <X className="size-4"></X>
              </Drawer.Header>
            </div>
          </div>
        </div>

        <Drawer.Body className="h-full p-0 overflow-y-auto">
          <div className="flex h-full">
            {/* Form Section - 100% width */}
            <div className="w-full h-full overflow-y-auto">
              <div className="h-full overflow-y-auto product-list">
                <div className="row">
                  <div className="col-xl-12">
                    <div className="card">
                      <div className="card-body">
                        {isLoading ? (
                          <Loader />
                        ) : (
                          <form
                            id="studentForm"
                            className="needs-validation"
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            <div className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                
                                {/* LEFT PANEL - Personal & Contact Information */}
                                <div className="space-y-6">
                                  
                                  {/* Personal Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        👤 Personal Information
                                      </h6>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="form-label" htmlFor="firstName">
                                          First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="firstName"
                                          name="firstName"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.firstName && validation.errors.firstName ? "is-invalid" : ""}`}
                                          placeholder="Enter first name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.firstName}
                                        />
                                        {validation.touched.firstName && validation.errors.firstName ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.firstName?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="lastName">
                                          Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="lastName"
                                          name="lastName"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.lastName && validation.errors.lastName ? "is-invalid" : ""}`}
                                          placeholder="Enter last name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.lastName}
                                        />
                                        {validation.touched.lastName && validation.errors.lastName ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.lastName?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="gender">
                                          Gender <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          id="gender"
                                          name="gender"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.gender && validation.errors.gender ? "is-invalid" : ""}`}
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.gender}
                                        >
                                          <option value="">Select gender</option>
                                          {genderOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                        {validation.touched.gender && validation.errors.gender ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.gender?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="email">
                                          Email
                                        </label>
                                        <input
                                          id="email"
                                          name="email"
                                          type="email"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.email && validation.errors.email ? "is-invalid" : ""}`}
                                          placeholder="Enter email address"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.email}
                                        />
                                        {validation.touched.email && validation.errors.email ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.email?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="dob">
                                          Date of Birth <span className="text-danger">*</span>
                                        </label>
                                        <Flatpickr
                                          id="dob"
                                          name="dob"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.dob && validation.errors.dob ? "is-invalid" : ""}`}
                                          placeholder="Select date of birth"
                                          options={{
                                            dateFormat: "Y-m-d",
                                            maxDate: "today",
                                            defaultDate: validation.values.dob || undefined
                                          }}
                                          onChange={(date: any) => {
                                            validation.setFieldValue(
                                              "dob",
                                              date.length > 0 ? moment(date[0]).format("YYYY-MM-DD") : ""
                                            );
                                          }}
                                          value={validation.values.dob || undefined}
                                        />
                                        {validation.touched.dob && validation.errors.dob ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.dob?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="startDate">
                                          Start Date <span className="text-danger">*</span>
                                        </label>
                                        <Flatpickr
                                          id="startDate"
                                          name="startDate"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.startDate && validation.errors.startDate ? "is-invalid" : ""}`}
                                          placeholder="Select start date"
                                          options={{
                                            dateFormat: "Y-m-d",
                                            defaultDate: validation.values.startDate || undefined
                                          }}
                                          onChange={(date: any) => {
                                            validation.setFieldValue(
                                              "startDate",
                                              date.length > 0 ? moment(date[0]).format("YYYY-MM-DD") : ""
                                            );
                                          }}
                                          value={validation.values.startDate || undefined}
                                        />
                                        {validation.touched.startDate && validation.errors.startDate ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.startDate?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                    </div>
                                  </div>

                                  {/* Contact Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        📞 Contact Information
                                      </h6>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="form-label" htmlFor="phone1">
                                          Primary Phone <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="phone1"
                                          name="phone1"
                                          type="text"
                                          maxLength={10}
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.phone1 && validation.errors.phone1 ? "is-invalid" : ""}`}
                                          placeholder="Enter primary phone number"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.phone1}
                                        />
                                        {validation.touched.phone1 && validation.errors.phone1 ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.phone1?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="phone2">
                                          Secondary Phone
                                        </label>
                                        <input
                                          id="phone2"
                                          name="phone2"
                                          type="text"
                                          maxLength={10}
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.phone2 && validation.errors.phone2 ? "is-invalid" : ""}`}
                                          placeholder="Enter secondary phone number"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.phone2}
                                        />
                                        {validation.touched.phone2 && validation.errors.phone2 ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.phone2?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="streetAddress">
                                          Street Address
                                        </label>
                                        <input
                                          id="streetAddress"
                                          name="streetAddress"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter street address"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.streetAddress}
                                        />
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="address1">
                                          Apartment/Unit
                                        </label>
                                        <input
                                          id="address1"
                                          name="address1"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter apartment/unit"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.address1}
                                        />
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="city">
                                          City <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="city"
                                          name="city"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.city && validation.errors.city ? "is-invalid" : ""}`}
                                          placeholder="Enter city"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.city}
                                        />
                                        {validation.touched.city && validation.errors.city ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.city?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="state">
                                          State <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          id="state"
                                          name="state"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.state && validation.errors.state ? "is-invalid" : ""}`}
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.state}
                                        >
                                          <option value="">Select state</option>
                                          {stateOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                        {validation.touched.state && validation.errors.state ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.state?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="zip">
                                          Zip Code <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="zip"
                                          name="zip"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.zip && validation.errors.zip ? "is-invalid" : ""}`}
                                          placeholder="Enter zip code"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.zip}
                                        />
                                        {validation.touched.zip && validation.errors.zip ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.zip?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* RIGHT PANEL - Guardian, Financial & Additional Information */}
                                <div className="space-y-6">
                                  
                                  {/* Guardian Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        👨‍👩‍👧‍👦 Guardian & Emergency Contact
                                      </h6>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="form-label" htmlFor="guardian">
                                          Guardian Name
                                        </label>
                                        <input
                                          id="guardian"
                                          name="guardian"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter guardian name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.guardian}
                                        />
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="guardianPhone">
                                          Guardian Phone
                                        </label>
                                        <input
                                          id="guardianPhone"
                                          name="guardianPhone"
                                          type="text"
                                          maxLength={10}
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.guardianPhone && validation.errors.guardianPhone ? "is-invalid" : ""}`}
                                          placeholder="Enter guardian phone"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.guardianPhone}
                                        />
                                        {validation.touched.guardianPhone && validation.errors.guardianPhone ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.guardianPhone?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="emergencyContactName">
                                          Emergency Contact Name
                                        </label>
                                        <input
                                          id="emergencyContactName"
                                          name="emergencyContactName"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter emergency contact name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.emergencyContactName}
                                        />
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="emergencyContactPhone">
                                          Emergency Contact Phone
                                        </label>
                                        <input
                                          id="emergencyContactPhone"
                                          name="emergencyContactPhone"
                                          type="text"
                                          maxLength={10}
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.emergencyContactPhone && validation.errors.emergencyContactPhone ? "is-invalid" : ""}`}
                                          placeholder="Enter emergency contact phone"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.emergencyContactPhone}
                                        />
                                        {validation.touched.emergencyContactPhone && validation.errors.emergencyContactPhone ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.emergencyContactPhone?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Academic & Financial Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        🎓 Academic & Financial Information
                                      </h6>
                                    </div>
                                    
                                    <div className="space-y-4">
                                      {/* First row: Program and Class */}
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                          <label className="form-label" htmlFor="programId">
                                            Program <span className="text-danger">*</span>
                                          </label>
                                          <Select
                                            id="programId"
                                            name="programId"
                                            options={programOptions}
                                            value={programOptions.find(option => option.value === validation.values.programId) || null}
                                            onChange={(selectedOption: any) => {
                                              validation.setFieldValue("programId", selectedOption ? selectedOption.value : "");
                                            }}
                                            onBlur={() => validation.setFieldTouched("programId", true)}
                                            placeholder="Select a program"
                                            isClearable
                                            isSearchable
                                            className={`react-select-container ${validation.touched.programId && validation.errors.programId ? "is-invalid" : ""}`}
                                            classNamePrefix="react-select"
                                            styles={{
                                              control: (provided: any, state: any) => ({
                                                ...provided,
                                                minHeight: '42px',
                                                borderColor: validation.touched.programId && validation.errors.programId 
                                                  ? '#ef4444' 
                                                  : state.isFocused 
                                                    ? '#8b5cf6' 
                                                    : '#e2e8f0',
                                                boxShadow: state.isFocused 
                                                  ? '0 0 0 1px #8b5cf6' 
                                                  : 'none',
                                                '&:hover': {
                                                  borderColor: validation.touched.programId && validation.errors.programId 
                                                    ? '#ef4444' 
                                                    : '#8b5cf6'
                                                }
                                              }),
                                              option: (provided: any, state: any) => ({
                                                ...provided,
                                                backgroundColor: state.isSelected 
                                                  ? '#8b5cf6' 
                                                  : state.isFocused 
                                                    ? '#f3f4f6' 
                                                    : 'white',
                                                color: state.isSelected ? 'white' : '#374151',
                                                '&:hover': {
                                                  backgroundColor: state.isSelected ? '#8b5cf6' : '#f3f4f6'
                                                }
                                              })
                                            }}
                                          />
                                          {validation.touched.programId && validation.errors.programId ? (
                                            <div className="mt-1 text-sm text-red-500">
                                              {validation.errors.programId?.toString()}
                                            </div>
                                          ) : null}
                                        </div>

                                        <div>
                                          <label className="form-label" htmlFor="classId">
                                            Class <span className="text-danger">*</span>
                                          </label>
                                          <Select
                                            id="classId"
                                            name="classId"
                                            options={classOptions}
                                            value={classOptions.find(option => option.value === validation.values.classId) || null}
                                            onChange={(selectedOption: any) => {
                                              validation.setFieldValue("classId", selectedOption ? selectedOption.value : "");
                                            }}
                                            onBlur={() => validation.setFieldTouched("classId", true)}
                                            placeholder="Select a class"
                                            isClearable
                                            isSearchable
                                            className={`react-select-container ${validation.touched.classId && validation.errors.classId ? "is-invalid" : ""}`}
                                            classNamePrefix="react-select"
                                            styles={{
                                              control: (provided: any, state: any) => ({
                                                ...provided,
                                                minHeight: '42px',
                                                borderColor: validation.touched.classId && validation.errors.classId 
                                                  ? '#ef4444' 
                                                  : state.isFocused 
                                                    ? '#8b5cf6' 
                                                    : '#e2e8f0',
                                                boxShadow: state.isFocused 
                                                  ? '0 0 0 1px #8b5cf6' 
                                                  : 'none',
                                                '&:hover': {
                                                  borderColor: validation.touched.classId && validation.errors.classId 
                                                    ? '#ef4444' 
                                                    : '#8b5cf6'
                                                }
                                              }),
                                              option: (provided: any, state: any) => ({
                                                ...provided,
                                                backgroundColor: state.isSelected 
                                                  ? '#8b5cf6' 
                                                  : state.isFocused 
                                                    ? '#f3f4f6' 
                                                    : 'white',
                                                color: state.isSelected ? 'white' : '#374151',
                                                '&:hover': {
                                                  backgroundColor: state.isSelected ? '#8b5cf6' : '#f3f4f6'
                                                }
                                              })
                                            }}
                                          />
                                          {validation.touched.classId && validation.errors.classId ? (
                                            <div className="mt-1 text-sm text-red-500">
                                              {validation.errors.classId?.toString()}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>

                                      {/* Second row: Registration Fee, Fees, and Discount */}
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                          <label className="form-label" htmlFor="registrationFee">
                                            Registration Fee <span className="text-danger">*</span>
                                          </label>
                                          <input
                                            id="registrationFee"
                                            name="registrationFee"
                                            type="number"
                                            step="0.01"
                                            className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.registrationFee && validation.errors.registrationFee ? "is-invalid" : ""}`}
                                            placeholder="Enter registration fee"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.registrationFee}
                                          />
                                          {validation.touched.registrationFee && validation.errors.registrationFee ? (
                                            <div className="mt-1 text-sm text-red-500">
                                              {validation.errors.registrationFee?.toString()}
                                            </div>
                                          ) : null}
                                        </div>

                                        <div>
                                          <label className="form-label" htmlFor="fees">
                                            Fees <span className="text-danger">*</span>
                                          </label>
                                          <input
                                            id="fees"
                                            name="fees"
                                            type="number"
                                            step="0.01"
                                            className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.fees && validation.errors.fees ? "is-invalid" : ""}`}
                                            placeholder="Enter fees"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.fees}
                                          />
                                          {validation.touched.fees && validation.errors.fees ? (
                                            <div className="mt-1 text-sm text-red-500">
                                              {validation.errors.fees?.toString()}
                                            </div>
                                          ) : null}
                                        </div>

                                        <div>
                                          <label className="form-label" htmlFor="discount">
                                            Discount
                                          </label>
                                          <input
                                            id="discount"
                                            name="discount"
                                            type="number"
                                            step="0.01"
                                            className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.discount && validation.errors.discount ? "is-invalid" : ""}`}
                                            placeholder="Enter discount"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.discount}
                                          />
                                          {validation.touched.discount && validation.errors.discount ? (
                                            <div className="mt-1 text-sm text-red-500">
                                              {validation.errors.discount?.toString()}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Additional Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        📝 Additional Information
                                      </h6>
                                    </div>
                                    
                                    <div className="space-y-4">
                                      <div>
                                        <label className="form-label" htmlFor="profilePic">
                                          Profile Picture URL
                                        </label>
                                        <input
                                          id="profilePic"
                                          name="profilePic"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter profile picture URL"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.profilePic}
                                        />
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="notes">
                                          Notes
                                        </label>
                                        <textarea
                                          id="notes"
                                          name="notes"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter notes (optional)"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.notes}
                                          rows={2}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </form>
                        )}
                        <ToastContainer closeButton={false} limit={1} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer.Body>
        
        <Drawer.Footer className="flex items-center justify-end p-4 border-t border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-600">
          <div className="flex gap-2">
            <button
              type="button"
              className="text-slate-500 btn bg-white border-slate-200 hover:text-slate-600 hover:bg-slate-100 hover:border-slate-200 focus:text-slate-600 focus:bg-slate-100 focus:border-slate-200 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-100 active:border-slate-200 active:ring active:ring-slate-100 dark:bg-zink-700 dark:hover:bg-zink-600 dark:border-zink-500 dark:text-zink-200"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="studentForm"
              className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <LoaderIcon className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                  Saving...
                </>
              ) : "Save"}
            </button>
          </div>
        </Drawer.Footer>
      </Drawer>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={() => {}}
        buttonCaption="Yes, Delete It!"
      />
    </React.Fragment>
  );
}

export default AddStudent;

